import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
    ProviderApiError,
    useProviderApi,
    useProviderContext,
} from "@/pages/Providers/useProviderApi";
import { providerResolver } from "@/pages/Providers/schema";
import { emptyProvider } from "@/pages/Providers/fields";
import type {
    Catalogs,
    Provider,
    ProviderConflict,
    ProviderValues,
} from "@/pages/Providers/types";

interface ProviderEditorOptions {
    catalogs: Catalogs;
    provider?: Provider;
    onSaved: (provider: Provider) => void;
}

export const useProviderEditor = ({
    catalogs,
    provider,
    onSaved,
}: ProviderEditorOptions) => {
    const { t, sendMessage } = useProviderContext();
    const { request } = useProviderApi();
    const form = useForm<ProviderValues>({
        defaultValues: emptyProvider(catalogs, provider),
        resolver: providerResolver,
        mode: "onBlur",
    });
    const [matches, setMatches] = useState<ProviderConflict[]>([]);
    const [observation, setObservation] = useState("");
    const [pending, setPending] = useState<ProviderValues | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const checkSequence = useRef(0);
    const initial = useRef(emptyProvider(catalogs, provider));
    const checkingSnapshot = useRef("");

    const showMatches = (conflicts: ProviderConflict[]): void => {
        setMatches(conflicts);
        if (conflicts.some((conflict) => conflict.reason === "tax")) {
            form.setError("tax_id", {
                type: "duplicate",
                message: "providers.errors.duplicate_tax",
            });
        } else {
            if (form.getFieldState("tax_id").error?.type === "duplicate") {
                form.clearErrors("tax_id");
            }
        }
    };

    const checkPayload = (values: ProviderValues): object => ({
        id: provider?.id,
        name: values.name,
        tax_id: values.tax_id,
        email: values.email,
        phone: values.phone,
        address: values.address,
        address_extra: values.address_extra,
        postal_code: values.postal_code,
        city: values.city,
        province: values.province,
        country_id: values.country_id,
    });
    const showError = (failure: unknown): void => {
        if (failure instanceof ProviderApiError) {
            const fields =
                failure.body.errors ?? failure.body.data?.errors ?? {};
            for (const [field, messages] of Object.entries(fields)) {
                form.setError(field.split(".")[0] as keyof ProviderValues, {
                    message: messages[0],
                });
            }
            if (failure.body.data?.matches?.length) {
                showMatches(failure.body.data.matches);
            }
            setError(
                failure.status === 409
                    ? "providers.errors.stale"
                    : "providers.errors.review"
            );
            return;
        }
        setError("providers.errors.request");
    };
    const checkIdentity = async (): Promise<void> => {
        const values = form.getValues();
        const payload = checkPayload(values);
        const snapshot = JSON.stringify(payload);
        if (
            !form.formState.isDirty ||
            snapshot === checkingSnapshot.current ||
            busy
        ) {
            return;
        }
        checkingSnapshot.current = snapshot;
        const sequence = ++checkSequence.current;
        try {
            const response = await request<{ matches: ProviderConflict[] }>(
                "/check",
                payload
            );
            if (
                sequence === checkSequence.current &&
                snapshot === JSON.stringify(checkPayload(form.getValues()))
            ) {
                setPending(null);
                showMatches(response.matches);
            }
        } catch (failure: unknown) {
            if (sequence === checkSequence.current) {
                showError(failure);
            }
        }
    };
    const persist = async (
        values: ProviderValues,
        confirmed: boolean,
        conflicts: ProviderConflict[] = []
    ): Promise<void> => {
        setBusy(true);
        setError(null);
        try {
            const response = await request<{ item: Provider }>(
                provider ? `/${provider.id}` : "",
                {
                    ...values,
                    ...(provider ? { version: provider.version } : {}),
                    warnings_confirmed: confirmed,
                    warning_fingerprints: conflicts
                        .map((conflict) => conflict.fingerprint)
                        .filter(Boolean),
                    observation,
                },
                undefined,
                provider ? "put" : "post"
            );
            sendMessage(t("providers.ui.saved"), "success");
            onSaved(response.item);
        } catch (failure: unknown) {
            setPending(values);
            showError(failure);
        } finally {
            setBusy(false);
        }
    };
    const submit = form.handleSubmit(async (values) => {
        ++checkSequence.current;
        setBusy(true);
        setError(null);
        try {
            const response = await request<{ matches: ProviderConflict[] }>(
                "/check",
                checkPayload(values)
            );
            if (response.matches.length) {
                setPending(values);
                showMatches(response.matches);
                return;
            }
            await persist(values, false);
        } catch (failure: unknown) {
            showError(failure);
        } finally {
            setBusy(false);
        }
    });
    const confirm = async (): Promise<void> => {
        if (matches.some((match) => match.severity === "blocking")) {
            return;
        }
        const values = pending ?? form.getValues();
        const valid = await form.trigger();
        if (!valid) {
            setMatches([]);
            return;
        }
        if (!pending) {
            setMatches([]);
            return;
        }
        await persist(values, true, matches);
    };

    return {
        form,
        matches,
        setMatches,
        observation,
        setObservation,
        busy,
        error,
        checkIdentity,
        submit,
        confirm,
        pending,
        initial: initial.current,
    };
};
